<template>
  <div
    class="shadow-chat-window relative flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden rounded-[20px] bg-white-card"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <ChatHeader
      :manager-name="managerName"
      :manager-online="managerOnline"
      :search-open="searchOpen"
      :search-text="searchText"
      :search-date-from="searchDateFrom"
      :search-date-to="searchDateTo"
      :search-loading="searchLoading"
      :search-error="searchError"
      @open-search="emit('openSearch')"
      @close-search="emit('closeSearch')"
      @update:search-text="emit('update:searchText', $event)"
      @update:search-date-from="emit('update:searchDateFrom', $event)"
      @update:search-date-to="emit('update:searchDateTo', $event)"
      @search="emit('search')"
    />
    <ChatMessages
      :messages="messages"
      :loading="loading"
      :error-text="errorText"
      :highlight-query="highlightQuery"
      :highlight-message-ids="highlightMessageIds"
    />
    <ChatInputBar
      :model-value="modelValue"
      :sending="sending"
      :uploading="uploadingFile"
      :can-upload-file="canUploadFile"
      :file-warning-text="fileWarningText"
      @update:model-value="emit('update:modelValue', $event)"
      @send="emit('send')"
      @attach-files="emit('attachFiles', $event)"
    />

    <div
      v-if="isDragOver"
      class="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-white/82 p-4 backdrop-blur-sm"
    >
      <div
        class="flex w-full max-w-[320px] flex-col items-center justify-center rounded-[20px] border border-dashed border-green-button/40 bg-white/96 px-5 py-8 text-center shadow-panel-soft"
      >
        <p class="text-[18px] font-helvetica-700 text-gray-dark">
          {{ dropZoneTitle }}
        </p>
        <p class="mt-2 text-[14px] leading-[20px] text-extra-text-color">
          {{ dropZoneHint }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import ChatHeader from "~/components/chat/ChatHeader.vue";
import ChatInputBar from "~/components/chat/ChatInputBar.vue";
import ChatMessages from "~/components/chat/ChatMessages.vue";
import type { HelpChatUiMessage } from "~/types/helpChat";

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    messages?: HelpChatUiMessage[];
    loading?: boolean;
    errorText?: string;
    managerName?: string;
    managerOnline?: boolean;
    sending?: boolean;
    uploadingFile?: boolean;
    canUploadFile?: boolean;
    fileWarningText?: string;
    searchOpen?: boolean;
    searchText?: string;
    searchDateFrom?: string;
    searchDateTo?: string;
    searchLoading?: boolean;
    searchError?: string;
    highlightQuery?: string;
    highlightMessageIds?: number[];
  }>(),
  {
    modelValue: "",
    messages: () => [],
    loading: false,
    errorText: "",
    managerName: "",
    managerOnline: false,
    sending: false,
    uploadingFile: false,
    canUploadFile: false,
    fileWarningText: "",
    searchOpen: false,
    searchText: "",
    searchDateFrom: "",
    searchDateTo: "",
    searchLoading: false,
    searchError: "",
    highlightQuery: "",
    highlightMessageIds: () => [],
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "send"): void;
  (e: "attachFiles", files: File[]): void;
  (e: "openSearch"): void;
  (e: "closeSearch"): void;
  (e: "update:searchText", value: string): void;
  (e: "update:searchDateFrom", value: string): void;
  (e: "update:searchDateTo", value: string): void;
  (e: "search"): void;
}>();

const isDragOver = ref(false);
const dragDepth = ref(0);

const dropZoneTitle = computed(() =>
  props.canUploadFile
    ? "Отпустите файлы для загрузки"
    : "Сначала отправьте сообщение",
);
const dropZoneHint = computed(() =>
  props.canUploadFile
    ? "Файлы будут отправлены в текущий чат"
    : "После первого сообщения можно прикреплять файлы",
);

const isBusy = computed(() => props.sending || props.uploadingFile);

const hasDraggedFiles = (dataTransfer?: DataTransfer | null) =>
  Array.from(dataTransfer?.types ?? []).includes("Files");

const resetDragState = () => {
  isDragOver.value = false;
  dragDepth.value = 0;
};

const handleDragEnter = (event: DragEvent) => {
  if (isBusy.value || !hasDraggedFiles(event.dataTransfer)) return;
  dragDepth.value += 1;
  isDragOver.value = true;
};

const handleDragOver = (event: DragEvent) => {
  if (isBusy.value || !hasDraggedFiles(event.dataTransfer)) return;
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "copy";
  }
  isDragOver.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  if (isBusy.value || !hasDraggedFiles(event.dataTransfer)) return;
  dragDepth.value = Math.max(0, dragDepth.value - 1);
  if (dragDepth.value === 0) {
    isDragOver.value = false;
  }
};

const handleDrop = (event: DragEvent) => {
  if (isBusy.value || !hasDraggedFiles(event.dataTransfer)) {
    resetDragState();
    return;
  }

  const files = Array.from(event.dataTransfer?.files ?? []);
  resetDragState();
  if (!files.length) return;
  emit("attachFiles", files);
};
</script>
